import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Plus, Search, ChevronRight, Briefcase, Building2, CalendarDays } from 'lucide-react';
import { motion } from 'framer-motion';
import { getJobs } from '../../../../../services/staffingService';
import CoverageBar from '../shared/CoverageBar';

const ITEMS_PER_PAGE = 20;

const STATUS_CONFIG = {
  open:      { label: 'Open',      colors: 'bg-red-50 text-red-700 border-red-200'       },
  disputed:  { label: 'Disputed',  colors: 'bg-amber-50 text-amber-700 border-amber-200' },
  completed: { label: 'Completed', colors: 'bg-gray-50 text-gray-600 border-gray-200'    },
  paid:      { label: 'Paid',      colors: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const SkeletonRow = () => (
  <tr>
    {[140, 140, 80, 120, 80].map((w, i) => (
      <td key={i} className="px-4 py-3">
        <div className={`h-4 animate-pulse rounded bg-muted`} style={{ width: w }} />
      </td>
    ))}
    <td className="px-4 py-3"><div className="h-4 w-5 animate-pulse rounded bg-muted" /></td>
  </tr>
);

const JobsTab = () => {
  const { providerCompanyId } = useParams();
  const history = useHistory();

  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter]       = useState('all');
  const [page, setPage]       = useState(1);

  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getJobs(providerCompanyId);
      setJobs(resp.data.jobs || []);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [providerCompanyId]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Filter
  const filtered = jobs.filter((j) => {
    const matchStatus = statusFilter === 'all' || j.status === statusFilter;
    const q = debouncedSearch.toLowerCase();
    const matchSearch = !q
      || j._id?.toLowerCase().includes(q)
      || j.customer_company_local_id?.companyFriendlyName?.toLowerCase().includes(q)
      || j.customer_company_local_id?.companyLegalName?.toLowerCase().includes(q)
      || j.location?.toLowerCase().includes(q)
      || j.serviceType?.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated  = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const getCustomerName = (job) =>
    job.customer_company_id?.companyFriendlyName
    || job.customer_company_id?.companyLegalName
    || '—';

  const getShiftDates = (job) => {
    if (!job.shifts?.length) return '—';
    const dates = job.shifts.map((s) => new Date(s.startTime)).filter(Boolean).sort((a, b) => a - b);
    if (!dates.length) return '—';
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return dates.length === 1 ? fmt(dates[0]) : `${fmt(dates[0])} – ${fmt(dates[dates.length - 1])}`;
  };

  return (
    <div>
      {/* Search + filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search jobs, customers…"
            className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-pink"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-pink"
        >
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="disputed">Disputed</option>
          <option value="completed">Completed</option>
          <option value="paid">Paid</option>
        </select>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => history.push(`/provider-company/${providerCompanyId}/dispatch/job/new`)}
            className="flex items-center gap-2 rounded-lg bg-brand-pink px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-pink/90"
          >
            <Plus size={15} />
            New Job
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                {['Job', 'Customer', 'Shifts', 'Officers', 'Status', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">
                    {debouncedSearch || statusFilter !== 'all' ? 'No jobs match your filters.' : 'No jobs yet. Create your first job to get started.'}
                  </td>
                </tr>
              ) : (
                paginated.map((job, idx) => {
                  const statusInfo = STATUS_CONFIG[job.status] || STATUS_CONFIG.open;
                  const stats      = job.statistics || {};
                  const covered    = stats.totalShiftsCovered ?? 0;
                  const total      = stats.totalStaffRequired ?? (job.shifts?.reduce((s, sh) => s + (sh.staffsRequired || 1), 0) ?? 0);
                  const openCount  = stats.offerCounts?.open ?? 0;
                  const acceptedCount = stats.offerCounts?.accepted ?? 0;
                  const lapsedCount = (stats.offerCounts?.expired ?? 0) + (stats.offerCounts?.rejected ?? 0);

                  return (
                    <motion.tr
                      key={job._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.03 }}
                      onClick={() => history.push(`/provider-company/${providerCompanyId}/dispatch/job/${job._id}`)}
                      className="cursor-pointer transition-colors hover:bg-muted/30"
                    >
                      {/* Job */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-primary/10">
                            <Briefcase size={14} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-card-foreground font-mono">
                              {job._id?.slice(-8).toUpperCase()}
                            </p>
                            <p className="text-[11px] text-muted-foreground">{job.serviceType || 'Security'}</p>
                            {job.hourlyPayRate && <p className="text-[10px] text-muted-foreground mt-0.5">${job.hourlyPayRate.toFixed(2)}/hr</p>}
                          </div>
                        </div>
                      </td>

                      {/* Customer */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Building2 size={13} className="text-muted-foreground flex-shrink-0" />
                          <div>
                            <span className="text-sm text-card-foreground truncate max-w-[160px] block">
                              {getCustomerName(job)}
                            </span>
                            {job.customer_company_id?.facilityType && <p className="text-[10px] text-muted-foreground mt-0.5">{job.customer_company_id.facilityType}</p>}
                          </div>
                        </div>
                      </td>

                      {/* Shifts */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-sm text-card-foreground">
                          <CalendarDays size={13} className="text-muted-foreground" />
                          <span>{total} shift{total !== 1 ? 's' : ''}</span>
                        </div>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">{getShiftDates(job)}</p>
                      </td>

                      {/* Officers */}
                      <td className="px-4 py-3 min-w-[180px]">
                        <div className="space-y-2">
                          <CoverageBar covered={covered} total={total} />
                          <div className="flex flex-wrap gap-1">
                            {openCount > 0 && <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 text-[10px] font-medium">{openCount} pending</span>}
                            {acceptedCount > 0 && <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-medium">{acceptedCount} confirmed</span>}
                            {lapsedCount > 0 && <span className="inline-flex items-center rounded-full bg-gray-50 text-gray-600 border border-gray-200 px-1.5 py-0.5 text-[10px] font-medium">{lapsedCount} lapsed</span>}
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${statusInfo.colors}`}>
                          {statusInfo.label}
                        </span>
                      </td>

                      {/* Arrow */}
                      <td className="px-4 py-3 text-muted-foreground">
                        <ChevronRight size={16} />
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>{filtered.length} jobs total</span>
          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="rounded border border-border px-3 py-1 text-xs disabled:opacity-40 hover:bg-muted transition-colors"
            >
              Prev
            </button>
            <span className="text-xs">{page} / {totalPages}</span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
              className="rounded border border-border px-3 py-1 text-xs disabled:opacity-40 hover:bg-muted transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsTab;
